<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ChannelHostingAPI from 'dashboard/api/channelHosting';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const loading = ref(false);
const reconnecting = ref(false);
const checkingProxy = ref(false);
const proxyVerified = ref(false);
const egressIp = ref('');
const proxyLatencyMs = ref(null);
const session = ref(null);
const proxyForm = ref({
  protocol: 'socks5',
  host: '',
  port: '',
  username: '',
  password: '',
});

const instanceName = computed(
  () =>
    props.inbox?.additional_attributes?.channel_hosting_instance ||
    props.inbox?.channel?.additional_attributes?.channel_hosting_instance
);

const showErrorBanner = computed(() => {
  if (!session.value) return false;
  return (
    session.value.status === 'error' ||
    session.value.circuit_open === true ||
    session.value.last_error === 'connection_conflict_circuit_open' ||
    session.value.last_error === 'gateway_instance_missing'
  );
});

const resetProxyCheck = () => {
  proxyVerified.value = false;
  egressIp.value = '';
  proxyLatencyMs.value = null;
};

const loadSession = async () => {
  loading.value = true;
  try {
    const { data } = await ChannelHostingAPI.getSessions();
    const list = data.payload || [];
    const found =
      list.find(s => s.inbox_id === props.inbox.id) ||
      list.find(s => s.instance_name === instanceName.value);
    session.value = found || null;
    if (found) {
      proxyForm.value = {
        protocol: found.proxy_protocol || 'socks5',
        host: found.proxy_host || '',
        port: found.proxy_port || '',
        username: found.proxy_username || '',
        password: '',
      };
      resetProxyCheck();
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.debug('[channel_hosting] settings load failed', e);
  } finally {
    loading.value = false;
  }
};

const refresh = async () => {
  if (!session.value?.id) return;
  try {
    const { data } = await ChannelHostingAPI.refresh(session.value.id);
    session.value = data.payload || data;
  } catch (e) {
    const payload = e?.response?.data?.payload;
    if (payload) session.value = payload;
    useAlert(e?.response?.data?.message || t('CHANNEL_HOSTING.ERRORS.CREATE'));
  }
};

const disconnect = async () => {
  if (!session.value?.id) return;
  const { data } = await ChannelHostingAPI.disconnect(session.value.id);
  session.value = data;
  useAlert(t('CHANNEL_HOSTING.DISCONNECT_DONE'));
};

const reconnect = async () => {
  if (!session.value?.id) return;
  reconnecting.value = true;
  try {
    const { data } = await ChannelHostingAPI.reconnect(session.value.id);
    session.value = data;
    useAlert(t('CHANNEL_HOSTING.RECONNECT_READY'));
  } catch (e) {
    const payload = e?.response?.data?.payload;
    if (payload) session.value = payload;
    useAlert(
      e?.response?.data?.message || t('CHANNEL_HOSTING.ERRORS.RECONNECT')
    );
  } finally {
    reconnecting.value = false;
  }
};

const removeHosting = async () => {
  if (!session.value?.id) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('CHANNEL_HOSTING.REMOVE_CONFIRM'))) return;
  try {
    await ChannelHostingAPI.deleteSession(session.value.id, {
      deleteInbox: true,
    });
    session.value = null;
    useAlert(t('CHANNEL_HOSTING.REMOVE_DONE'));
  } catch (e) {
    useAlert(e?.response?.data?.message || t('CHANNEL_HOSTING.ERRORS.CREATE'));
  }
};

const checkProxy = async () => {
  if (!proxyForm.value.host || !proxyForm.value.port) {
    useAlert(t('CHANNEL_HOSTING.ERRORS.PROXY_REQUIRED'));
    return;
  }
  checkingProxy.value = true;
  resetProxyCheck();
  try {
    const { data } = await ChannelHostingAPI.checkProxy({
      protocol: proxyForm.value.protocol,
      host: proxyForm.value.host.trim(),
      port: Number(proxyForm.value.port),
      username: proxyForm.value.username,
      password: proxyForm.value.password,
      session_id: session.value?.id,
    });
    if (data?.ok && data?.egress_ip) {
      proxyVerified.value = true;
      egressIp.value = data.egress_ip;
      proxyLatencyMs.value = data.latency_ms;
      useAlert(t('CHANNEL_HOSTING.PROXY_OK'));
    } else {
      useAlert(data?.message || t('CHANNEL_HOSTING.ERRORS.PROXY_CHECK'));
    }
  } catch (e) {
    useAlert(e?.response?.data?.message || t('CHANNEL_HOSTING.ERRORS.PROXY_CHECK'));
  } finally {
    checkingProxy.value = false;
  }
};

const saveProxy = async () => {
  if (!session.value?.id) return;
  if (!proxyVerified.value) {
    useAlert(t('CHANNEL_HOSTING.PROXY_CHECK_REQUIRED'));
    return;
  }
  try {
    const { data } = await ChannelHostingAPI.updateProxy(session.value.id, {
      protocol: proxyForm.value.protocol,
      host: proxyForm.value.host,
      port: Number(proxyForm.value.port),
      username: proxyForm.value.username,
      password: proxyForm.value.password || undefined,
    });
    session.value = data;
    useAlert(t('CHANNEL_HOSTING.SAVE_PROXY'));
  } catch (e) {
    useAlert(e?.response?.data?.message || t('CHANNEL_HOSTING.ERRORS.CREATE'));
  }
};

onMounted(loadSession);
watch(
  proxyForm,
  () => {
    resetProxyCheck();
  },
  { deep: true }
);
</script>

<template>
  <div class="flex flex-col gap-6 max-w-lg">
    <div>
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('CHANNEL_HOSTING.SETTINGS_CONNECTION') }}
      </h3>
      <p class="text-sm text-n-slate-11 mt-1">
        {{ t('CHANNEL_HOSTING.PAIR_HINT') }}
      </p>
    </div>

    <div
      v-if="showErrorBanner"
      class="rounded-xl border border-n-ruby-5 bg-n-ruby-2 px-4 py-3 text-sm text-n-ruby-12"
    >
      {{ t('CHANNEL_HOSTING.ERROR_BANNER') }}
    </div>

    <div
      v-if="session"
      class="rounded-xl border border-n-weak px-4 py-3 text-sm flex flex-col gap-2"
    >
      <div class="flex justify-between">
        <span class="text-n-slate-11">{{ t('CHANNEL_HOSTING.STATUS') }}</span>
        <span class="font-medium text-n-slate-12">{{ session.status }}</span>
      </div>
      <div
        v-if="session.phone_number"
        class="flex justify-between gap-4"
      >
        <span class="text-n-slate-11">{{ t('CHANNEL_HOSTING.PHONE') }}</span>
        <span class="font-medium text-n-slate-12 tabular-nums">{{
          session.phone_number
        }}</span>
      </div>
      <div class="flex justify-between gap-4">
        <span class="text-n-slate-11">Proxy</span>
        <span class="font-medium text-n-slate-12 truncate">{{
          session.proxy_summary || '—'
        }}</span>
      </div>
      <div
        v-if="session.qr"
        class="mt-2 flex justify-center rounded-xl border border-n-weak bg-white p-3"
      >
        <img
          :src="
            String(session.qr).startsWith('data:')
              ? session.qr
              : `data:image/png;base64,${String(session.qr).replace(/^data:image\/\w+;base64,/, '')}`
          "
          alt="QR"
          class="w-40 h-40 object-contain"
        />
      </div>
      <div class="flex flex-wrap gap-2 mt-2">
        <Button
          :label="t('CHANNEL_HOSTING.REFRESH')"
          slate
          sm
          @click="refresh"
        />
        <Button
          :label="t('CHANNEL_HOSTING.DISCONNECT')"
          slate
          sm
          :disabled="session.status === 'disconnected'"
          @click="disconnect"
        />
        <Button
          :label="
            reconnecting
              ? t('CHANNEL_HOSTING.RECONNECTING')
              : t('CHANNEL_HOSTING.RECONNECT')
          "
          sm
          :is-loading="reconnecting"
          @click="reconnect"
        />
        <Button
          :label="t('CHANNEL_HOSTING.REMOVE_SESSION')"
          slate
          sm
          @click="removeHosting"
        />
      </div>
    </div>
    <p v-else-if="!loading" class="text-sm text-n-slate-11">
      {{ t('CHANNEL_HOSTING.WAITING_QR') }}
    </p>

    <div class="flex flex-col gap-3">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('CHANNEL_HOSTING.SETTINGS_PROXY') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('CHANNEL_HOSTING.PROXY_HINT') }}
      </p>
      <div class="grid grid-cols-2 gap-3">
        <label class="text-sm flex flex-col gap-1">
          <span>{{ t('CHANNEL_HOSTING.PROXY_PROTOCOL') }}</span>
          <select
            v-model="proxyForm.protocol"
            class="rounded-lg border border-n-weak px-3 py-2 bg-n-alpha-black2"
          >
            <option value="socks5">SOCKS5</option>
            <option value="http">HTTP</option>
            <option value="https">HTTPS</option>
          </select>
        </label>
        <label class="text-sm flex flex-col gap-1">
          <span>{{ t('CHANNEL_HOSTING.PROXY_PORT') }}</span>
          <input
            v-model="proxyForm.port"
            type="number"
            class="rounded-lg border border-n-weak px-3 py-2 bg-n-alpha-black2"
          />
        </label>
        <label class="text-sm flex flex-col gap-1 col-span-2">
          <span>{{ t('CHANNEL_HOSTING.PROXY_HOST') }}</span>
          <input
            v-model="proxyForm.host"
            type="text"
            class="rounded-lg border border-n-weak px-3 py-2 bg-n-alpha-black2"
            autocomplete="off"
          />
        </label>
        <label class="text-sm flex flex-col gap-1">
          <span>{{ t('CHANNEL_HOSTING.PROXY_USER') }}</span>
          <input
            v-model="proxyForm.username"
            type="text"
            class="rounded-lg border border-n-weak px-3 py-2 bg-n-alpha-black2"
            autocomplete="off"
          />
        </label>
        <label class="text-sm flex flex-col gap-1">
          <span>{{ t('CHANNEL_HOSTING.PROXY_PASS') }}</span>
          <input
            v-model="proxyForm.password"
            type="password"
            class="rounded-lg border border-n-weak px-3 py-2 bg-n-alpha-black2"
            autocomplete="new-password"
          />
        </label>
      </div>

      <div
        v-if="proxyVerified && egressIp"
        class="rounded-xl border border-n-weak px-4 py-3 text-sm flex flex-col gap-1"
      >
        <div class="flex justify-between gap-3">
          <span class="text-n-slate-11">{{ t('CHANNEL_HOSTING.PROXY_OK') }}</span>
          <span v-if="proxyLatencyMs != null" class="tabular-nums text-n-slate-11"
            >{{ proxyLatencyMs }} ms</span
          >
        </div>
        <div class="flex justify-between gap-3">
          <span class="text-n-slate-11">{{ t('CHANNEL_HOSTING.PROXY_EGRESS_IP') }}</span>
          <span class="font-medium tabular-nums">{{ egressIp }}</span>
        </div>
      </div>

      <div class="flex flex-wrap gap-2">
        <Button
          :label="
            checkingProxy
              ? t('CHANNEL_HOSTING.CHECKING_PROXY')
              : t('CHANNEL_HOSTING.CHECK_PROXY')
          "
          slate
          :is-loading="checkingProxy"
          :disabled="session?.status === 'connected'"
          @click="checkProxy"
        />
        <Button
          :label="t('CHANNEL_HOSTING.SAVE_PROXY')"
          :disabled="session?.status === 'connected' || !proxyVerified"
          @click="saveProxy"
        />
      </div>
    </div>
  </div>
</template>
